import React, { useState, useEffect } from 'react';
import { usePIM } from '../../context/PIMContext';
import { ConfirmationModal } from '../ui/ConfirmationModal';

const DatabaseManager = () => {
    const { dbMode, fetchDbMode } = usePIM();
    const [products, setProducts] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [promoteResult, setPromoteResult] = useState(null);
    const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);

    // Initial fetch handled by Context now

    useEffect(() => {
        if (dbMode === 'test') {
            fetchProducts();
        } else {
            setProducts([]);
        }
    }, [dbMode]);

    const fetchProducts = async () => {
        try {
            // Fetch recent products from current DB (Test)
            const res = await fetch('/api/products?status=Development&limit=50');
            // Note: API might not support limit, but it returns all. 
            // We filter for display.
            const data = await res.json();
            setProducts(data);
        } catch (err) {
            console.error("Failed to fetch products", err);
        }
    };

    const toggleMode = async (newMode) => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin/set-db-mode', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode: newMode })
            });
            const data = await res.json();
            if (data.success) {
                // Update global state
                await fetchDbMode();
                setPromoteResult(null); // Clear previous results
                window.location.reload();
            }
        } catch (err) {
            console.error("Failed to set DB mode", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePromoteClick = () => {
        if (selectedIds.length === 0) return;
        setIsPromoteModalOpen(true);
    };

    const handleConfirmPromote = async () => {
        setLoading(true);
        setIsPromoteModalOpen(false); // Close modal immediately or wait? Better close and show loading
        try {
            const res = await fetch('/api/admin/promote', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ products: selectedIds })
            });
            const data = await res.json();
            setPromoteResult(data);
            if (data.success) {
                setSelectedIds([]);
                // Optional: refresh list
            }
        } catch (err) {
            console.error("Promotion failed", err);
            setPromoteResult({ success: false, error: err.message });
        } finally {
            setLoading(false);
        }
    };

    const toggleSelect = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(i => i !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    return (
        <div className="p-6 text-stone-200 space-y-8">
            {/* DB Switcher */}
            <div className={`p-6 rounded-lg border ${dbMode === 'test' ? 'bg-amber-900/20 border-amber-600' : 'bg-green-900/10 border-green-800'}`}>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    Current Environment:
                    <span className={dbMode === 'test' ? 'text-amber-500' : 'text-green-500'}>
                        {dbMode.toUpperCase()}
                    </span>
                </h2>

                <div className="flex gap-4">
                    <button
                        onClick={() => toggleMode('prod')}
                        disabled={loading || dbMode === 'prod'}
                        className={`px-4 py-2 rounded font-medium transition-all ${dbMode === 'prod'
                            ? 'bg-green-600 text-white cursor-default'
                            : 'bg-stone-800 hover:bg-stone-700 text-stone-400'
                            }`}
                    >
                        PRODUCTION
                    </button>
                    <button
                        onClick={() => toggleMode('test')}
                        disabled={loading || dbMode === 'test'}
                        className={`px-4 py-2 rounded font-medium transition-all ${dbMode === 'test'
                            ? 'bg-amber-600 text-white cursor-default'
                            : 'bg-stone-800 hover:bg-stone-700 text-stone-400'
                            }`}
                    >
                        TEST ENVIRONMENT
                    </button>
                </div>

                {dbMode === 'test' && (
                    <div className="mt-4 text-amber-200 text-sm">
                        ⚠️ You are in TEST MODE. Changes here do not affect the live website until promoted.<br />
                        File uploads are stored in <code>/uploads/pim_test</code>.
                    </div>
                )}
            </div>

            {/* Promotion UI */}
            <div className="bg-stone-900/50 p-6 rounded-xl border border-stone-800">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-white">Promote to Production</h3>
                    <button
                        onClick={handlePromoteClick}
                        disabled={loading || selectedIds.length === 0}
                        className={`px-6 py-2 rounded-lg font-bold shadow-lg transition-all ${selectedIds.length > 0
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:scale-105'
                            : 'bg-stone-800 text-stone-500 cursor-not-allowed'
                            }`}
                    >
                        {loading ? 'Processing...' : `Promote Selected (${selectedIds.length})`}
                    </button>
                </div>

                {promoteResult && (
                    <div className={`mb-6 p-4 rounded border ${promoteResult.success ? 'bg-green-900/20 border-green-600 text-green-200' : 'bg-red-900/20 border-red-600 text-red-200'}`}>
                        <h4 className="font-bold">{promoteResult.success ? 'Promotion Successful!' : 'Promotion Failed'}</h4>
                        <pre className="text-xs mt-2 whitespace-pre-wrap">{JSON.stringify(promoteResult, null, 2)}</pre>
                    </div>
                )}

                {dbMode !== 'test' ? (
                    <div className="text-center py-12 text-stone-500">
                        Switch to TEST mode to select products for promotion.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="text-stone-400 border-b border-stone-700">
                                <tr>
                                    <th className="p-3 w-10">Select</th>
                                    <th className="p-3">ID</th>
                                    <th className="p-3">SKU</th>
                                    <th className="p-3">Model</th>
                                    <th className="p-3">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-800">
                                {products.map(p => (
                                    <tr key={p.id} className="hover:bg-stone-800/50 transition-colors">
                                        <td className="p-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(p.id)}
                                                onChange={() => toggleSelect(p.id)}
                                                className="w-4 h-4 rounded border-stone-600 bg-stone-700 text-blue-600 focus:ring-blue-500"
                                            />
                                        </td>
                                        <td className="p-3 text-stone-500">{p.id}</td>
                                        <td className="p-3 font-mono text-stone-300">{p.sku}</td>
                                        <td className="p-3 text-white">{p.model_name}</td>
                                        <td className="p-3">
                                            <span className={`px-2 py-0.5 rounded text-xs ${p.status === 'Ready' ? 'bg-green-900 text-green-300' : 'bg-stone-700 text-stone-300'}`}>
                                                {p.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {products.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-stone-500">
                                            No products found in Test DB.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={isPromoteModalOpen}
                onClose={() => setIsPromoteModalOpen(false)}
                onConfirm={handleConfirmPromote}
                title="Promote Products"
                messages={[
                    "Are you sure you want to promote the following products to PRODUCTION?",
                    `Count: ${selectedIds.length}`,
                    "This will overwrite any existing production data for these SKUs."
                ]}
            />
        </div>
    );
};

export default DatabaseManager;
